"use client";

import { useEffect, useRef, useState } from "react";
import { Mic, Waves, SlidersHorizontal as Sliders, Network, Radio, Award } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
type Stage = {
  number: string;
  title: string;
  description: string;
  icon: "mic" | "waves" | "sliders" | "network" | "radio" | "award";
};
const stages: Stage[] = [{
  number: "01",
  title: "Material Analysis",
  description: "Rigorous evaluation of substrate properties, adhesive compatibility, and environmental performance requirements for your specific industrial application.",
  icon: "mic"
}, {
  number: "02",
  title: "Precision Engineering",
  description: "Custom die-cutting and slitting configurations designed to maximize material yield and ensure exact tolerances for high-speed assembly lines.",
  icon: "waves"
}, {
  number: "03",
  title: "Prototyping Phase",
  description: "Rapid development of precision samples using our advanced die-cutting systems to validate fit, form, and function before full-scale production.",
  icon: "radio"
}, {
  number: "04",
  title: "Advanced Laminating",
  description: "Multi-layer lamination processes that integrate films, foams, and adhesives into high-performance composite structures for aerospace and automotive use.",
  icon: "sliders"
}, {
  number: "05",
  title: "Quality Assurance",
  description: "Stringent inspection protocols and production efficiency monitoring to ensure every component meets the highest North American manufacturing standards.",
  icon: "network"
}, {
  number: "06",
  title: "Production & Delivery",
  description: "Finalized production runs executed with precision, followed by secure, efficient logistics to support your supply chain requirements.",
  icon: "award"
}];
function StageIcon({
  name,
  className
}: {
  name: Stage["icon"];
  className?: string;
}) {
  const props = {
    className,
    strokeWidth: 1.25
  };
  switch (name) {
    case "mic":
      return <Mic {...props} />;
    case "waves":
      return <Waves {...props} />;
    case "sliders":
      return <Sliders {...props} />;
    case "network":
      return <Network {...props} />;
    case "radio":
      return <Radio {...props} />;
    case "award":
      return <Award {...props} />;
  }
}
function StageBlock({
  stage,
  index,
  isFinal
}: {
  stage: Stage;
  index: number;
  isFinal: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25,
    rootMargin: "0px 0px -80px 0px"
  });
  const isOdd = index % 2 === 0;
  const offsetClass = index === 0 ? "lg:mt-0" : index === 1 ? "lg:mt-20" : index === 2 ? "lg:mt-20" : index === 3 ? "lg:mt-20" : index === 4 ? "lg:mt-20" : "lg:mt-24";
  const words = stage.description.split(" ");
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-y-6 ${offsetClass}`}>
      {}
      <div className="hidden lg:flex lg:col-start-1 lg:col-span-12 absolute left-0 lg:left-[calc(33.333%-8px)] top-2 items-center justify-center pointer-events-none">
        <span className={`block rounded-lg transition-all duration-700 ${isFinal ? "h-5 w-5" : "h-4 w-4"}`} style={{
        backgroundColor: "var(--primary)",
        boxShadow: isFinal ? "0 0 0 6px hsla(262, 48%, 30%, 0.18), 0 0 32px 4px hsla(262, 48%, 30%, 0.45)" : "0 0 0 4px hsla(262, 48%, 30%, 0.15), 0 0 16px 2px hsla(262, 48%, 30%, 0.35)",
        opacity: isVisible ? 1 : 0.35,
        transform: isVisible ? "scale(1)" : "scale(0.6)"
      }} />
      </div>

      {}
      <div className="lg:hidden absolute left-0 top-2 flex items-center justify-center pointer-events-none">
        <span className={`block rounded-lg ${isFinal ? "h-4 w-4" : "h-3 w-3"}`} style={{
        backgroundColor: "var(--primary)",
        boxShadow: isFinal ? "0 0 0 5px hsla(262, 48%, 30%, 0.18), 0 0 24px 3px hsla(262, 48%, 30%, 0.45)" : "0 0 0 3px hsla(262, 48%, 30%, 0.15), 0 0 12px 2px hsla(262, 48%, 30%, 0.35)"
      }} />
      </div>

      {}
      <div className={`pl-8 lg:pl-0 ${isOdd ? "lg:col-start-6 lg:col-end-12" : "lg:col-start-2 lg:col-end-8"}`}>
        <div className="flex items-start gap-5">
          <div className="font-serif italic text-5xl md:text-6xl leading-none shrink-0" style={{
          color: "var(--primary)",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(12px)",
          transition: "opacity 700ms ease, transform 700ms ease"
        }}>
            {stage.number}
          </div>
          <div className="flex-1">
            <div className="mb-4 inline-flex items-center justify-center rounded-lg p-2 border border-border" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 700ms ease 80ms, transform 700ms ease 80ms"
          }}>
              <StageIcon name={stage.icon} className="size-5 text-primary" />
            </div>
            <h3 className="font-default font-semibold text-2xl md:text-3xl leading-tight text-foreground mb-3" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 700ms ease 140ms, transform 700ms ease 140ms"
          }}>
              {stage.title}
            </h3>
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-md">
              {words.map((w, wi) => <span key={wi} className="inline-block" style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(8px)",
              transition: `opacity 500ms ease ${200 + wi * 35}ms, transform 500ms ease ${200 + wi * 35}ms`,
              marginRight: "0.28em"
            }}>
                  {w}
                </span>)}
            </p>

            {isFinal && <div className="mt-6" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 700ms ease 600ms, transform 700ms ease 600ms"
          }}>
                <Button asChild size="lg" className="rounded-lg font-default text-base bg-primary text-white">
                  <Link to="/ContactPage">Request a Quote</Link>
                </Button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}
export default function Manufacturing() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height + vh;
      const traveled = vh - rect.top;
      const pct = Math.max(0, Math.min(1, traveled / total));
      setProgress(pct);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4510" ref={sectionRef} id="our-manufacturing-journey" className="bg-background text-foreground py-24 md:py-48 px-6 md:px-10 overflow-hidden relative">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headRef} className="mb-20 md:mb-28 max-w-4xl" style={{
        opacity: headVisible ? 1 : 0,
        transform: headVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 800ms ease, transform 800ms ease"
      }}>
          <div className="flex items-center gap-3 mb-8">
            <span className="block h-px w-8" style={{
            backgroundColor: "var(--primary)"
          }} />
            <span className="font-mono text-xs uppercase tracking-widest text-primary">
              Process
            </span>
          </div>
          <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight text-foreground">
            Our Manufacturing Journey
          </h2>
          <p className="mt-8 font-default text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
            Precision-driven workflows designed for industrial excellence. From initial material analysis to final delivery, we ensure quality at every step.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="lg:hidden absolute left-[7px] top-0 bottom-0 w-px" style={{
          backgroundColor: "var(--border)"
        }} />
          <div className="lg:hidden absolute left-[7px] top-0 w-px origin-top" style={{
          backgroundColor: "var(--primary)",
          height: `${progress * 100}%`,
          boxShadow: "0 0 8px hsla(262, 48%, 30%, 0.5)"
        }} />

          {}
          <div className="hidden lg:block absolute top-0 bottom-0 w-px" style={{
          left: "calc(33.333% - 0.5px)",
          backgroundColor: "var(--border)"
        }} />
          <div className="hidden lg:block absolute top-0 w-px origin-top" style={{
          left: "calc(33.333% - 0.5px)",
          backgroundColor: "var(--primary)",
          height: `${progress * 100}%`,
          boxShadow: "0 0 10px hsla(262, 48%, 30%, 0.5)"
        }} />

          <div className="space-y-20 md:space-y-24 relative">
            {stages.map((stage, i) => <StageBlock key={stage.number} stage={stage} index={i} isFinal={i === stages.length - 1} />)}
          </div>
        </div>
      </div>
    </section>;
}